import { useMemo, useState } from 'react'
import { gerarArquivoExcel } from '../../../../services/util/excel.service'
import { getTps } from '../../../../services/api/https/settings/tp.http'
import {
  CloneUserSettingsToAnotherUser,
  DeactivateMultipleUserByIds,
  GetUsersByTp,
  getUsers,
} from '../../../../services/api/https/settings/user.http'

const INITIAL_STATE = {
  tps: [],
  selectedTp: null,
  users: [],
  activeIndex: 0,
  usersSelected: [],
  allUsers: [],
  selectedBaseUser: null,
  selectedTargetUser: null,
  errorMessage: null,
  successMessage: null,
  flagUserGroup: false,
  flagCompanies: false,
  flagTps: false,
  flagOpCenter: false,
  flagPatrimonio: false,
  flagGroupCompanies: false,
  confirmAction: () => {},
  confirmIsVisible: false,
  loadingMessage: null,
}

const SETTINGS_FLAGS = {
  userGroup: 0,
  userCompanies: 1,
  userTps: 2,
  userOpCenters: 3,
  userPatrimonioOwner: 4,
  userCompaniesGroup: 5,
}

export default function useConfiguracoesDeUsuario() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    const setIsLoading = loadingMessage =>
      setState(prevState => ({ ...prevState, loadingMessage: loadingMessage }))
    const endLoading = () => setState(prevState => ({ ...prevState, loadingMessage: null }))
    const landErrorMessage = error =>
      setState(prevState => ({ ...prevState, errorMessage: error, confirmIsVisible: false }))
    const landSuccessMessage = msg => setState(prevState => ({ ...prevState, successMessage: msg }))
    const landConfirmModal = confirmAction =>
      setState(prevState => ({
        ...prevState,
        confirmIsVisible: true,
        confirmAction: confirmAction,
      }))

    return {
      //Requests
      getUserByTp: async selectedTp => {
        await GetUsersByTp(selectedTp)
          .then(users => setState(prevState => ({ ...prevState, users: users })))
          .catch(landErrorMessage)
          .finally(endLoading)
      },

      getAllTps: async () => {
        setIsLoading('Adquirindo TPs')
        getTps()
          .then(tps => setState(prevState => ({ ...prevState, tps: tps })))
          .catch(landErrorMessage)
          .finally(endLoading)
      },

      getAllUsers: async () => {
        setIsLoading('Adquirindo todos os usuarios')
        getUsers()
          .then(users =>
            setState(prevState => ({
              ...prevState,
              allUsers: users.map(u => ({ label: `${u.id} - ${u.name}`, value: u.id })),
            })),
          )
          .catch(landErrorMessage)
          .finally(endLoading)
      },

      handleActiveStateOfMultipleUserByIds: async (tpId, body) => {
        setIsLoading('Atualizando usuarios')
        await DeactivateMultipleUserByIds(tpId, body)
          .then(users => {
            landSuccessMessage('Usuários atualizados com sucesso')
            setState(prevState => ({ ...prevState, users: users, usersSelected: [] }))
          })
          .catch(landErrorMessage)
          .finally(endLoading)
      },

      getUsuariosFromTp: async selectedTp => {
        setIsLoading('Adquirindo usuarios')
        localDispatch.getUserByTp(selectedTp).finally(endLoading)
      },

      cloneUserSettingsToAnotherUser: async (selectedBaseUser, selectedTargetUser, flags) => {
        setIsLoading('Clonando usuario')
        await CloneUserSettingsToAnotherUser(selectedBaseUser, selectedTargetUser, flags)
          .then(res => {
            landSuccessMessage(res)
            setState(prevState => ({
              ...prevState,
              selectedTargetUser: null,
              confirmIsVisible: false,
            }))
          })
          .catch(landErrorMessage)
          .finally(endLoading)
      },

      //Events
      onTabChange: (index, tps, allUsers) => {
        setState(prevState => ({ ...prevState, activeIndex: index }))
        if (index === 0 && !tps.length) {
          localDispatch.getAllTps()
        } else if (index === 1 && !allUsers.length) {
          localDispatch.getAllUsers()
        }
      },

      onClickDesativar: async (selectedTp, usersSelected) => {
        if (!selectedTp)
          landErrorMessage('Selecione um Operador Logístico para manipular os usuários')
        else if (!usersSelected.length)
          landErrorMessage('Você precisa selecionar pelo menos um usuário para manipular')
        else
          localDispatch.handleActiveStateOfMultipleUserByIds(selectedTp.id, {
            usersIds: usersSelected.map(u => u.id),
            actived: false,
          })
      },

      onClickAtivar: async (selectedTp, usersSelected) => {
        if (!selectedTp)
          landErrorMessage('Selecione um Operador Logístico para manipular os usuários')
        else if (!usersSelected.length)
          landErrorMessage('Você precisa selecionar pelo menos um usuário para manipular')
        else
          localDispatch.handleActiveStateOfMultipleUserByIds(selectedTp.id, {
            usersIds: usersSelected.map(u => u.id),
            actived: true,
          })
      },

      onClickExportar: async (selectedTp, usersSelected) => {
        if (!selectedTp)
          landErrorMessage('Selecione um Operador Logístico para exportar os usuários')
        else if (!usersSelected.length)
          landErrorMessage('Você precisa selecionar pelo menos um usuário para exportar')
        else {
          const rows = [
            [
              'Código',
              'Nome',
              'E-mail',
              'Ativo'
            ],
          ]

          usersSelected.forEach(p => {
            const {
              id,
              name,
              email,
              actived
            } = p
            rows.push([
              id,
              name,
              email,
              actived ? 'Sim' : 'Não'
            ])
          })
          gerarArquivoExcel('usuarios', rows, 'usuarios.xlsx')
        }
      },

      onClickClonar: async (
        flagUserGroup,
        flagCompanies,
        flagTps,
        flagOpCenter,
        flagPatrimonio,
        flagGroupCompanies,
        selectedBaseUser,
        selectedTargetUser,
      ) => {
        const flags = []

        if (flagUserGroup) flags.push(SETTINGS_FLAGS.userGroup)
        if (flagCompanies) flags.push(SETTINGS_FLAGS.userCompanies)
        if (flagTps) flags.push(SETTINGS_FLAGS.userTps)
        if (flagOpCenter) flags.push(SETTINGS_FLAGS.userOpCenters)
        if (flagPatrimonio) flags.push(SETTINGS_FLAGS.userPatrimonioOwner)
        if (flagGroupCompanies) flags.push(SETTINGS_FLAGS.userCompaniesGroup)

        if (!selectedBaseUser || !selectedTargetUser)
          landErrorMessage('Preencha os dois seletores antes de tentar clonar as informações')
        else if (!flags.length) landErrorMessage('Selecione pelo menos uma opção a ser clonada')
        else
          landConfirmModal(() =>
            localDispatch.cloneUserSettingsToAnotherUser(
              selectedBaseUser,
              selectedTargetUser,
              flags,
            ),
          )
      },

      onTpSelectedChange: tp => setState(prevState => ({ ...prevState, selectedTp: tp })),
      onSelectedBaseUserChange: user =>
        setState(prevState => ({ ...prevState, selectedBaseUser: user })),
      onSelectedTargetUserChange: user =>
        setState(prevState => ({ ...prevState, selectedTargetUser: user })),
      onCheckBoxChange: (flagName, value) =>
        setState(prevState => {
          prevState[flagName] = value
          return { ...prevState }
        }),
      onConfirmCancel: () => setState(prevState => ({ ...prevState, confirmIsVisible: false })),
      resetState: () =>
        setState(prevState => ({
          ...prevState,
          loadingMessage: null,
          errorMessage: null,
          successMessage: null,
        })),

      // Helpers
      setSelections: value => setState(prevState => ({ ...prevState, usersSelected: value })),
    }
  }, [])

  return { state, localDispatch }
}
